import { Component } from 'react'
import { connect, ConnectedProps } from 'react-redux'
import { extentFeat } from '../../constants'
import { MbMap, ReduxState } from '../../types'

/**
 * ExtentMask
 * Adds a semi-transparent grey mask outside the LJ.json extent.
 *
 * Implementation details:
 * - We construct a single Polygon with two rings:
 *   - Outer ring: a large "world" rectangle
 *   - Inner ring: the LJ extent ring (as a hole)
 * - Mapbox GL will render the fill for the area between the outer ring and the hole,
 *   effectively greying out everything outside the LJ extent.
 */
class ExtentMask extends Component<PropsFromRedux & { map?: MbMap }> {
  layerId = 'extent-mask-layer'
  sourceId = 'extent-mask-source'

  componentDidMount() {
    // @ts-ignore - map is injected from Map parent to all children
    const { map } = this.props
    if (!map) return
    this.addOrReload(map)
  }

  componentDidUpdate(prevProps: PropsFromRedux) {
    // @ts-ignore - map is injected from Map parent to all children
    const { map } = this.props
    if (!map) return

    // Re-add after basemap style change
    if (this.props.basemapChangeId !== prevProps.basemapChangeId) {
      this.addOrReload(map)
    }
  }

  componentWillUnmount() {
    // @ts-ignore - map is injected from Map parent to all children
    const { map } = this.props
    if (!map) return
    this.removeLayerAndSource(map)
  }

  addOrReload(map: any) {
    this.removeLayerAndSource(map)
    const maskFeature = this.buildMaskFeature()
    map.addSource(this.sourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [maskFeature],
      },
    })
    map.addLayer({
      id: this.layerId,
      source: this.sourceId,
      type: 'fill',
      paint: {
        'fill-color': '#808080',
        'fill-opacity': 0.4,
        'fill-antialias': true,
      },
    })
    // Ensure the mask sits above the basemap but below our interactive layers
    try {
      map.moveLayer(this.layerId, 'road-label') // best-effort if style has such a layer
    } catch (e) {
      // Silently ignore if the reference layer doesn't exist
    }
  }

  removeLayerAndSource(map: any) {
    if (map.getLayer(this.layerId)) {
      map.removeLayer(this.layerId)
    }
    if (map.getSource(this.sourceId)) {
      map.removeSource(this.sourceId)
    }
  }

  buildMaskFeature() {
    // Outer world ring (roughly world bounds excluding the poles to avoid singularities)
    const outerRing: [number, number][] = [
      [-179.9, -85],
      [179.9, -85],
      [179.9, 85],
      [-179.9, 85],
      [-179.9, -85],
    ]

    // LJ extent first ring
    const ljRings = (extentFeat.geometry && extentFeat.geometry.coordinates) || []
    const ljOuterRing = Array.isArray(ljRings) && ljRings.length > 0 ? ljRings[0] : []

    const innerRing = this.ensureClosedRing(this.reverseRing(ljOuterRing as [number, number][]))

    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [outerRing, innerRing],
      },
    }
  }

  ensureClosedRing(ring: [number, number][]) {
    if (ring.length === 0) return ring
    const first = ring[0]
    const last = ring[ring.length - 1]
    if (first[0] === last[0] && first[1] === last[1]) {
      return ring
    }
    return [...ring, first]
  }

  reverseRing(ring: [number, number][]) {
    // Reverse winding to ensure correct hole orientation
    return [...ring].reverse()
  }

  render() {
    return null
  }
}

const mapStateToProps = (state: ReduxState) => ({
  basemapChangeId: state.map.basemapChangeId,
})

const connector = connect(mapStateToProps, {})
type PropsFromRedux = ConnectedProps<typeof connector>
export default connector(ExtentMask)
